/* Tiny i18n context — EN ↔ DA. Stores the choice in localStorage so it
   persists across navigations and reloads. */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import translations from "../i18n/translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "youfit_lang";

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "en" || saved === "da") return saved;
      // Auto-detect Danish browsers
      const browser = (navigator.language || "").toLowerCase();
      if (browser.startsWith("da")) return "da";
    } catch { /* ignore */ }
    return "en";
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
    // Update the <html lang> attribute for accessibility & SEO
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
    }
  }, [lang]);

  const t = useCallback((key, fallback) => {
    const dict = translations[lang] || translations.en;
    if (key in dict) return dict[key];
    // Try the other language as fallback so untranslated keys still render
    if (translations.en[key]) return translations.en[key];
    return fallback ?? key;
  }, [lang]);

  const toggle = useCallback(() => setLang((l) => (l === "en" ? "da" : "en")), []);

  return (
    <LanguageContext.Provider value={{ lang, setLang, toggle, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
}
